/**
 * ProtectedRoute — gates app routes behind authentication.
 *
 * Behaviour:
 *   - No token → redirect to /auth/login?next=<current path>.
 *   - Authenticated but activeDepartmentId is null → redirect to /auth/login
 *     (the LoginPage will render the dept picker since memberships are
 *     already in the store).
 *   - Otherwise → render the route children.
 *
 * `requiredRoles` is an optional array of effective-role strings the user
 * must hold to enter (the role of their active membership — not the global
 * user.role).  Failing this redirects to /app/chat with an error toast,
 * so the user always lands somewhere they CAN see.
 */
import { Navigate, useLocation } from "react-router-dom";

import { useAuthStore } from "../stores/authStore";
import { toast } from "../stores/toastStore";

export default function ProtectedRoute({ children, requiredRoles }) {
  const location           = useLocation();
  const isAuthenticated    = useAuthStore((s) => s.isAuthenticated());
  const activeDepartmentId = useAuthStore((s) => s.activeDepartmentId);
  const memberships        = useAuthStore((s) => s.memberships);

  // Not signed in at all → bounce to login with a return path.
  if (!isAuthenticated) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/auth/login?next=${next}`} replace />;
  }

  // Signed in but no department picked yet (multi-membership user) → login picker.
  if (activeDepartmentId == null) {
    return <Navigate to="/auth/login" replace />;
  }

  // Optional role gate.
  if (requiredRoles && requiredRoles.length > 0) {
    const active = memberships.find((m) => m.department_id === activeDepartmentId);
    const role   = active?.role || "chat_only";
    if (!requiredRoles.includes(role)) {
      toast.error(`That page requires role: ${requiredRoles.join(" or ")}.`);
      return <Navigate to="/app/chat" replace />;
    }
  }

  return children;
}
